import { Fragment, type KeyboardEvent, type ReactNode } from "react";
import type {
  CopilotChoice,
  CopilotFinding,
  CopilotPatch,
  CopilotReadout,
  CopilotRuleChange,
  CopilotStoreResult,
} from "@CommonApi/merchflows/_merchflowId_/config-copilot";
import { TELL_O_COPY } from "@CommonUtils/pog/tellO";
import { Color, Flex, Text } from "src/elements";
import { useTranslation } from "src/i18n";
import { styledSafe } from "src/utils/styledComponent";
import { OMark as OSummaryMark } from "src/components/PlanogramRules/components/OSummary";
import {
  changeKey,
  fixLabelOf,
  type CardState,
  type CopilotEntry,
  type CopilotIdleSwitch,
} from "../store/useConfigCopilot";

const GOOD_SCORE = 80;

export const OMark = ({ size = 32 }: { size?: number }) => (
  <OSummaryMark aria-hidden="true" style={{ width: size, height: size }} />
);

const BubbleElement = styledSafe("div")`
  padding: 10px 14px;
  border-radius: 4px 14px 14px 14px;
  background: ${Color.opacitySky};
  border: 1px solid ${Color.opacitySkyHover};
  font-size: 14px;
  line-height: 20px;
  color: ${Color.textMain};
`;

/** Words the model is about to reword: dimmed in place, so the swap does not move the layout. */
export const Wording = styledSafe("span")`
  transition: opacity 0.3s ease;

  &[aria-busy="true"] {
    opacity: 0.55;
  }
`;

export const OSays = ({ children, size = 26 }: { children: ReactNode; size?: number }) => (
  <Flex gap="10px" align="start">
    <OMark size={size} />
    <BubbleElement>{children}</BubbleElement>
  </Flex>
);

const UserBubble = styledSafe("div")`
  max-width: 80%;
  padding: 8px 12px;
  border-radius: 14px 4px 14px 14px;
  background: ${Color.greenSmoke};
  font-size: 13px;
  line-height: 18px;
  color: ${Color.textMain};
`;

export const UserSays = ({ children }: { children: ReactNode }) => (
  <Flex justify="right">
    <UserBubble data-testid="copilot-said">{children}</UserBubble>
  </Flex>
);

export const LinkButton = styledSafe("button")`
  padding: 0;
  border: none;
  background: none;
  color: ${Color.primary};
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    color: ${Color.primaryActive};
    text-decoration: underline;
  }

  &:disabled {
    color: ${Color.textSecondary};
    cursor: default;
    text-decoration: none;
  }
`;

export const OButton = styledSafe("button")<{ isPrimary?: boolean }>`
  display: inline-block;
  padding: 5px 12px;
  border: 1px solid ${({ isPrimary }) => (isPrimary ? Color.primary : Color.greenSmokeHover)};
  border-radius: 14px;
  background: ${({ isPrimary }) => (isPrimary ? Color.primary : Color.white)};
  color: ${({ isPrimary }) => (isPrimary ? Color.white : Color.primaryActive)};
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${({ isPrimary }) => (isPrimary ? Color.primaryActive : Color.greenSmoke)};
  }

  &[aria-expanded="true"] {
    background: ${Color.greenSmoke};
  }

  &:focus-visible {
    outline: 2px solid ${Color.primaryActive};
    outline-offset: 1px;
  }
`;

export const Section = ({
  title,
  aside,
  children,
}: {
  title: string;
  aside?: ReactNode;
  children: ReactNode;
}) => (
  <Flex column gap="8px">
    <Flex justify="between" align="center">
      <Text variant="small3" color={Color.textSecondary} textTransform="uppercase">
        {title}
      </Text>
      {aside}
    </Flex>
    {children}
  </Flex>
);

const Segment = styledSafe("button")<{ isSelected: boolean }>`
  position: relative;
  padding: 6px 12px;
  border: 1px solid ${({ isSelected }) => (isSelected ? Color.primary : Color.spaceGray)};
  background: ${({ isSelected }) => (isSelected ? Color.primary : Color.white)};
  color: ${({ isSelected }) => (isSelected ? Color.white : Color.textMain)};
  font: inherit;
  font-size: 12px;
  font-weight: ${({ isSelected }) => (isSelected ? 600 : 400)};
  white-space: nowrap;
  cursor: ${({ isSelected }) => (isSelected ? "default" : "pointer")};

  &:first-child {
    border-radius: 4px 0 0 4px;
  }

  &:last-child {
    border-radius: 0 4px 4px 0;
  }

  & + & {
    margin-left: -1px;
  }

  &:hover:not([aria-disabled="true"]) {
    border-color: ${Color.primary};
    z-index: 1;
  }

  &:focus-visible {
    outline: 2px solid ${Color.primaryActive};
    outline-offset: 1px;
    z-index: 2;
  }

  &[aria-disabled="true"] {
    cursor: default;
  }
`;

const PickBadge = styledSafe("span")`
  display: inline-block;
  margin-left: 6px;
  padding: 0 5px;
  border-radius: 7px;
  background: ${Color.primary};
  color: ${Color.white};
  font-size: 9px;
  font-weight: 700;
  line-height: 14px;
  vertical-align: 1px;
`;

const Hidden = styledSafe("span")`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: pre;
`;

const Dot = styledSafe("span")<{ color: string }>`
  display: inline-block;
  flex-shrink: 0;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ color }) => color};
`;

type Option = CopilotChoice["options"][number];

/** A "What I set" row: shown on its switch when one is listed, else on its own. */
export interface OSetRow {
  key: string;
  label: string;
  paths: readonly string[];
  value?: string;
  line?: string;
  isKept?: boolean;
  isWording?: boolean;
  action?: { label: string; onClick: () => void };
}

const choicePaths = (choice: CopilotChoice) =>
  choice.options.flatMap((option) => option.patch?.ops.map(({ path }) => path) ?? []);

const isRowOf = (row: OSetRow, choice: CopilotChoice) =>
  row.label === choice.label || choicePaths(choice).some((path) => row.paths.includes(path));

const SetLine = ({ row }: { row: OSetRow }) =>
  row.line || row.action ? (
    <Flex gap="8px" align="baseline" flexWrap="wrap">
      {row.line && (
        <Text variant="small1" color={Color.textSecondary}>
          <Wording data-testid="o-set-line" aria-busy={row.isWording}>
            {row.line}
          </Wording>
        </Text>
      )}
      {row.action && (
        <LinkButton type="button" onClick={row.action.onClick}>
          {row.action.label}
        </LinkButton>
      )}
    </Flex>
  ) : null;

interface SetupProps {
  title?: string;
  rows?: OSetRow[];
  choices: CopilotChoice[];

  idle?: CopilotIdleSwitch[];
  alwaysOn: string[];
  onPick: (choice: CopilotChoice, option: Option) => void;
}

const ARROWS: Record<string, number> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };

export const OSetup = ({ title, rows = [], choices, idle = [], alwaysOn, onPick }: SetupProps) => {
  const { t } = useTranslation("common");
  const rowOf = (choice: CopilotChoice) => rows.find((row) => isRowOf(row, choice));
  const ownRows = rows.filter((row) => !choices.some((choice) => isRowOf(row, choice)));

  const idleAfter = (id: string | null) =>
    idle.filter(({ after }) =>
      id === null ? !choices.some((choice) => choice.id === after) : after === id,
    );
  const idleRows = (id: string | null) =>
    idleAfter(id).map((row) => (
      <Flex
        key={row.id}
        gap="12px"
        align="start"
        aria-disabled="true"
        data-testid={`o-choice-${row.id}`}
      >
        <Flex width="170px" flexShrink={0} padding="7px 0 0 0">
          <Text variant="body2" bold color={Color.textSecondary}>
            {row.label}
          </Text>
        </Flex>
        <Flex flexGrow padding="7px 0 0 0">
          <Text variant="small1" color={Color.textSecondary}>
            {row.reason}
          </Text>
        </Flex>
      </Flex>
    ));

  const onArrow = (event: KeyboardEvent<HTMLDivElement>, choice: CopilotChoice) => {
    const step = ARROWS[event.key];
    if (!step) return;
    event.preventDefault();
    const radios = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('[role="radio"]'));
    const at = Math.max(0, radios.indexOf(document.activeElement as HTMLElement));
    const next = (at + step + radios.length) % radios.length;
    radios[next]?.focus();
    onPick(choice, choice.options[next]);
  };

  return (
    <Section title={title ?? t("configCopilot.setup")}>
      <Flex column gap="12px" data-testid="o-setup">
        {ownRows.map((row) => (
          <Flex key={row.key} gap="12px" align="start" data-testid="o-set-row">
            <Flex width="170px" flexShrink={0} align="center" gap="2px" padding="1px 0 0 0">
              <Text variant="body2" bold>
                {row.label}
              </Text>
              {row.isKept && <PickBadge>{t("configCopilot.pick")}</PickBadge>}
            </Flex>
            {/* One line: value, reason, Undo; the options wait for Adjust. */}
            <Flex gap="8px" align="baseline" flexWrap="wrap" flexGrow>
              {row.value && <Text variant="body2">{row.value}</Text>}
              <SetLine row={row} />
            </Flex>
          </Flex>
        ))}
        {choices.map((choice) => {
          const isOff = choice.tone === "attention";
          const labelId = `o-choice-label-${choice.id}`;
          const hasSelected = choice.options.some((option) => option.selected);

          const picked = choice.options.findIndex((option) => option.pick);
          const row = rowOf(choice);
          return (
            <Fragment key={choice.id}>
              <Flex gap="12px" align="start" data-testid={`o-choice-${choice.id}`}>
                <Flex width="170px" flexShrink={0} align="center" gap="6px" padding="7px 0 0 0">
                  {isOff && <Dot color={Color.yellow} />}
                  <Text variant="body2" bold>
                    <span id={labelId}>{choice.label}</span>
                  </Text>
                </Flex>

                <Flex column gap="4px" flexGrow>
                  <div
                    role="radiogroup"
                    aria-labelledby={labelId}
                    style={{ display: "flex" }}
                    onKeyDown={(event) => onArrow(event, choice)}
                  >
                    {choice.options.map((option, index) => (
                      <Segment
                        key={option.label}
                        type="button"
                        role="radio"
                        aria-checked={option.selected}
                        aria-disabled={!option.selected && !option.patch}
                        tabIndex={option.selected || (!hasSelected && index === 0) ? 0 : -1}
                        isSelected={option.selected}
                        onClick={() => onPick(choice, option)}
                      >
                        {option.label}
                        {index === picked && !option.selected && (
                          <>
                            {/* Read and copied as "Drop it, O's pick"; the badge's margin is the gap on screen. */}
                            <Hidden>, </Hidden>
                            <PickBadge>{t("configCopilot.pick")}</PickBadge>
                          </>
                        )}
                      </Segment>
                    ))}
                  </div>

                  {row && <SetLine row={row} />}
                  {/* O's reason replaces the note, unless the note is a warning. */}
                  {choice.note && (!row || isOff) && (
                    <Text variant="small1" color={isOff ? Color.yellowActive : Color.textSecondary}>
                      {choice.note}
                    </Text>
                  )}
                </Flex>
              </Flex>
              {idleRows(choice.id)}
            </Fragment>
          );
        })}
        {idleRows(null)}

        {alwaysOn.length > 0 && (
          <Flex
            padding="8px 10px"
            background={Color.lightGray}
            borderRadius="4px"
            data-testid="o-always-on"
          >
            <Text variant="small1" color={Color.textSecondary}>
              <strong>{t("configCopilot.alwaysOn")}:</strong> {alwaysOn.join(" · ")}
            </Text>
          </Flex>
        )}
      </Flex>
    </Section>
  );
};

const unnamedStoresOf = (finding: CopilotFinding) =>
  (finding.stores ?? []).filter((store) => !finding.title.includes(store));

const titleOf = (finding: CopilotFinding) => {
  const named = unnamedStoresOf(finding).slice(0, 2);
  return named.length > 0 ? `${finding.title} · ${named.join(", ")}` : finding.title;
};

const OtherStores = ({ finding }: { finding: CopilotFinding }) => {
  const { t } = useTranslation("common");
  const other = unnamedStoresOf(finding).slice(2);
  return other.length > 0 ? (
    <details data-testid="o-other-stores">
      <summary>{t("configCopilot.moreStores", { count: other.length })}</summary>
      <Text variant="small1" color={Color.textSecondary}>
        {other.join(", ")}
      </Text>
    </details>
  ) : null;
};

interface FixesProps {
  findings: CopilotFinding[];
  onFix: (finding: CopilotFinding) => void;
}

const isFixFirst = ({ severity, fix }: CopilotFinding) =>
  severity === "blocker" || (fix?.ops.length ?? 0) > 0;

export const OFixes = ({ findings, onFix }: FixesProps) => {
  const { t } = useTranslation("common");
  if (findings.length === 0) return null;

  return (
    <Section
      title={t(findings.some(isFixFirst) ? "configCopilot.fixFirst" : "configCopilot.worthKnowing")}
    >
      <Flex column gap="6px" data-testid="o-fixes">
        {findings.map((finding) => (
          <Flex
            key={finding.id}
            gap="8px"
            align="center"
            justify="between"
            padding="8px 10px"
            background={Color.alertMediumBg}
            border={`1px solid ${Color.alertMediumBorder}`}
            borderRadius="4px"
            data-testid="copilot-finding"
            data-source={finding.source}
          >
            <Flex gap="8px" align="center">
              <Dot color={finding.severity === "blocker" ? Color.alertHighIcon : Color.yellow} />
              <Flex column gap="3px">
                <Text variant="small1" color={Color.alertMediumText}>
                  {titleOf(finding)}
                </Text>
                {finding.detail && (
                  <Text variant="small1" color={Color.textSecondary}>
                    {finding.detail}
                  </Text>
                )}
                <OtherStores finding={finding} />
              </Flex>
            </Flex>

            {finding.fix && (
              <LinkButton type="button" onClick={() => onFix(finding)}>
                {fixLabelOf(finding, t("configCopilot.fix"))}
              </LinkButton>
            )}
          </Flex>
        ))}
      </Flex>
    </Section>
  );
};

interface ChangesProps {
  changes: CopilotRuleChange[];

  sinceRun: boolean;
  onUndo: (change: CopilotRuleChange) => void;
  onBack: () => void;
}

export const OChanges = ({ changes, sinceRun, onUndo, onBack }: ChangesProps) => {
  const { t } = useTranslation("common");
  if (changes.length === 0) return null;

  return (
    <Section
      title={t(sinceRun ? "configCopilot.changedSinceRun" : "configCopilot.changedFromSetup")}
      aside={
        <LinkButton type="button" onClick={onBack} data-testid="o-back-to-start">
          {t(sinceRun ? "configCopilot.backToLastRun" : "configCopilot.backToSetup")}
        </LinkButton>
      }
    >
      <Flex column gap="4px" data-testid="o-changes">
        {changes.map((change) => (
          <Flex key={changeKey(change)} gap="6px" align="center" data-testid="o-change">
            <Text variant="small1">
              {change.label}: {change.fromText} → {change.toText}
            </Text>
            <Text variant="small1" color={Color.textSecondary}>
              ·
            </Text>
            <LinkButton type="button" onClick={() => onUndo(change)}>
              {TELL_O_COPY.undo}
            </LinkButton>
          </Flex>
        ))}
      </Flex>
    </Section>
  );
};

const LEFT_OUT = "products_left_out";

const WALL_TOO_SMALL = "range_does_not_fit_the_wall";

type Translate = ReturnType<typeof useTranslation>["t"];

// A product can contribute to several causes, so these counts need not sum to the total.
const leftOutWhyOf = (
  findings: readonly CopilotFinding[],
  stores: readonly CopilotStoreResult[],
  t: Translate,
) => {
  const tooSmall = findings.find((finding) => finding.id === WALL_TOO_SMALL)?.stores ?? [];
  const read = stores.filter(
    (store) =>
      store.status === "DONE" &&
      store.unranged > 0 &&
      store.leftOutWhy &&
      !tooSmall.includes(store.store),
  );
  const sum = (count: (store: CopilotStoreResult) => number) =>
    read.reduce((total, store) => total + count(store), 0);
  const why = (key: keyof NonNullable<CopilotStoreResult["leftOutWhy"]>) =>
    sum((store) => store.leftOutWhy?.[key] ?? 0);
  const protectedOf = (key: "protected" | "products") =>
    sum((store) => store.protectedOf?.[key] ?? 0);
  const of = read.some((store) => store.protectedOf)
    ? ` (${t("configCopilot.leftOutProtectedOf", {
        protected: protectedOf("protected"),
        products: protectedOf("products"),
      })})`
    : "";
  const said = (count: number, line: string) => (count > 0 ? [line] : []);
  const unprotected = why("unprotected");
  return [
    ...said(unprotected, `${t("configCopilot.leftOutUnprotected", { count: unprotected })}${of}`),
    ...said(why("widthShort"), t("configCopilot.leftOutWidthShort", { count: why("widthShort") })),
    ...said(why("supply"), t("configCopilot.leftOutSupply", { count: why("supply") })),
    ...said(why("broken"), t("configCopilot.leftOutBroken", { count: why("broken") })),
    ...said(
      why("beforeSolve"),
      t("configCopilot.leftOutBeforeSolve", { count: why("beforeSolve") }),
    ),
  ].join(" · ");
};

const FindingRow = ({
  finding,
  causes,
  onFix,
}: {
  finding: CopilotFinding;

  causes?: string;
  onFix: (finding: CopilotFinding) => void;
}) => {
  const { t } = useTranslation("common");
  return (
    <Flex gap="6px" align="start" justify="between" padding="4px 0 0 0" data-testid="o-detail">
      <Flex gap="6px" align="start">
        <Flex padding="5px 0 0 0">
          <Dot color={finding.severity === "limit" ? Color.gray500 : Color.yellow} />
        </Flex>
        <Flex column>
          <Text variant="small1">{titleOf(finding)}</Text>
          {finding.detail && (
            <Text variant="small1" color={Color.textSecondary}>
              {finding.detail}
            </Text>
          )}
          <OtherStores finding={finding} />
          {causes && (
            <Text variant="small1" color={Color.textSecondary}>
              <span data-testid="o-left-out-why">{causes}</span>
            </Text>
          )}
        </Flex>
      </Flex>
      {finding.fix && (
        <LinkButton type="button" onClick={() => onFix(finding)}>
          {fixLabelOf(finding, t("configCopilot.fix"))}
        </LinkButton>
      )}
    </Flex>
  );
};

interface LastRunProps {
  details: { readout: CopilotReadout | null; findings: CopilotFinding[] };
  onFix: (finding: CopilotFinding) => void;
}

const Cell = ({
  children,
  width,
  color,
  bold,
}: {
  children: ReactNode;
  width?: string;
  color?: string;
  bold?: boolean;
}) => (
  <Flex width={width} flexGrow={width ? 0 : 1}>
    <Text variant="small1" color={color} bold={bold}>
      {children}
    </Text>
  </Flex>
);

const scoreColor = (score: number | null) => {
  if (score === null) return Color.textSecondary;
  return score >= GOOD_SCORE ? Color.primary : Color.alertCriticalText;
};

export const OLastRun = ({ details, onFix }: LastRunProps) => {
  const { t } = useTranslation("common");
  const stores = details.readout?.stores ?? [];

  const hasScores = stores.some((store) => store.aesthetics !== null);

  const hasFill = stores.some((store) => store.fill !== undefined);

  const hasPastSupply = stores.some((store) => (store.pastSupply ?? 0) > 0);

  const skipped = details.readout?.studioPlanSkipped ?? 0;

  return (
    <Flex
      column
      gap="8px"
      padding="8px 12px"
      border={`1px solid ${Color.lightGrayHover}`}
      borderRadius="6px"
      data-testid="o-last-run"
    >
      <Text variant="small1" color={Color.textSecondary}>
        {t("configCopilot.lastRunStores")}
      </Text>

      {details.readout && (
        <Flex column gap="3px" data-testid="copilot-readout">
          <Flex gap="6px">
            <Cell width="28%" color={Color.textSecondary}>
              {t("configCopilot.store")}
            </Cell>
            <Cell color={Color.textSecondary}>{t("configCopilot.placed")}</Cell>
            <Cell color={Color.textSecondary}>{t("configCopilot.missing")}</Cell>
            {/* Low and bare apart: O's line counts low shelves without the bare ones. */}
            <Cell color={Color.textSecondary}>{t("configCopilot.lowShelves")}</Cell>
            <Cell color={Color.textSecondary}>{t("configCopilot.bareShelves")}</Cell>
            {hasFill && <Cell color={Color.textSecondary}>{t("configCopilot.fill")}</Cell>}
            {hasPastSupply && (
              <Cell color={Color.textSecondary}>{t("configCopilot.pastSupply")}</Cell>
            )}
            {hasScores && <Cell color={Color.textSecondary}>{t("configCopilot.score")}</Cell>}
          </Flex>

          {stores.map((store) => (
            <Flex key={store.store} column gap="3px">
              <Flex gap="6px">
                <Cell width="28%">
                  {store.store}
                  {store.copiedFromBase && (
                    <span data-testid="o-copied-base"> · {t("configCopilot.copiedBase")}</span>
                  )}
                </Cell>
                {store.status === "DONE" ? (
                  <>
                    <Cell>{store.measured?.shelves === false ? "–" : store.placed}</Cell>
                    <Cell color={store.unranged > 0 ? Color.alertCriticalText : undefined}>
                      {store.measured?.products === false ? "–" : store.unranged}
                    </Cell>
                    <Cell color={store.lowShelves > 0 ? Color.yellowActive : undefined}>
                      {store.measured?.shelves === false ? "–" : store.lowShelves}
                    </Cell>
                    <Cell color={store.bareShelves > 0 ? Color.yellowActive : undefined}>
                      {store.measured?.shelves === false ? "–" : store.bareShelves}
                    </Cell>
                    {hasFill && (
                      <Cell>
                        {store.fill === undefined ? "–" : `${Math.round(store.fill * 100)}%`}
                      </Cell>
                    )}
                    {hasPastSupply && <Cell>{store.pastSupply ?? "–"}</Cell>}
                    {hasScores && (
                      <Cell bold color={scoreColor(store.aesthetics)}>
                        {store.aesthetics ?? "–"}
                      </Cell>
                    )}
                  </>
                ) : (
                  <Cell color={Color.textSecondary}>
                    {store.status === "ERROR"
                      ? t("configCopilot.failed")
                      : t("configCopilot.running")}
                  </Cell>
                )}
              </Flex>
              {store.status === "ERROR" && store.error && (
                <details data-testid="o-store-error">
                  <summary>{t("configCopilot.technicalError", { store: store.store })}</summary>
                  <pre style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                    {store.error}
                  </pre>
                </details>
              )}
            </Flex>
          ))}

          {skipped > 0 && (
            <Text variant="small1" color={Color.textSecondary}>
              <span data-testid="o-studio-plan-skipped">
                {skipped === 1
                  ? t("configCopilot.studioPlanSkippedOne")
                  : t("configCopilot.studioPlanSkippedMany", { count: skipped })}
              </span>
            </Text>
          )}

          {details.findings.map((finding) => (
            <FindingRow
              key={finding.id}
              finding={finding}
              causes={
                finding.id === LEFT_OUT ? leftOutWhyOf(details.findings, stores, t) : undefined
              }
              onFix={onFix}
            />
          ))}
        </Flex>
      )}
    </Flex>
  );
};

interface ChangeProps {
  patch: CopilotPatch;

  state: CardState;

  isMadeByO?: boolean;
  onApply: () => void;
  onUndo: () => void;
}

export const isRedoPatch = (patch: CopilotPatch) =>
  patch.id.startsWith("undo_") || patch.title.startsWith("Redo:");

type MadeEntry = CopilotEntry & Required<Pick<CopilotEntry, "applied">>;

const pathsOf = (patch: CopilotPatch) =>
  patch.ops
    .map(({ path }) => path)
    .sort()
    .join(" ");

export const undoneBy = (
  entries: readonly CopilotEntry[],
  redo: CopilotEntry,
): MadeEntry | undefined => {
  if (!redo.applied || !isRedoPatch(redo.applied.patch)) return undefined;
  const { patch } = redo.applied;
  return entries
    .filter(
      (entry): entry is MadeEntry =>
        entry.id < redo.id && entry.applied !== undefined && !isRedoPatch(entry.applied.patch),
    )
    .reverse()
    .find(
      ({ applied }) =>
        patch.id === `undo_${applied.patch.id}` || pathsOf(patch) === pathsOf(applied.patch),
    );
};

export const redoTitleOf = (
  entries: readonly CopilotEntry[],
  redo: MadeEntry,
  redoLabel = "Redo",
) => {
  const { patch, changes } = redo.applied;
  const isTouched = (row: CopilotRuleChange) =>
    row.paths.some((path) => patch.ops.some((op) => op.path === path));
  const undone = undoneBy(entries, redo)?.applied.changes?.filter(isTouched) ?? [];
  const rows =
    undone.length > 0
      ? undone
      : (changes ?? []).map((row) => ({ ...row, fromText: row.toText, toText: row.fromText }));
  if (rows.length === 0) return patch.title;
  return `${redoLabel}: ${rows.map((row) => `${row.label}, ${row.fromText} → ${row.toText}`).join("; ")}`;
};

export const OChange = ({ patch, state, isMadeByO, onApply, onUndo }: ChangeProps) => {
  const { t } = useTranslation("common");

  const said = [patch.expect, patch.cost].filter(Boolean).join(" · ");
  const isApplied = state === "on";
  const useLabel = state === "again" ? t("configCopilot.useAgain") : t("configCopilot.use");
  const undoLabel = isRedoPatch(patch) ? t("configCopilot.redo") : TELL_O_COPY.undo;

  return (
    <Flex
      justify="between"
      align="center"
      gap="12px"
      padding="8px 12px"
      border={`1px solid ${isApplied ? Color.primary : Color.lightGrayHover}`}
      background={isApplied ? Color.greenSmokeDisabled : Color.white}
      borderRadius="6px"
      data-testid="copilot-patch"
    >
      <Flex column gap="2px">
        <Text variant="small1" bold>
          {isApplied && !isMadeByO ? `${t("configCopilot.done")}: ` : ""}
          {patch.title}
        </Text>
        {said && (
          <Text variant="small1" color={Color.textSecondary}>
            {said}
          </Text>
        )}
      </Flex>

      <LinkButton type="button" onClick={isApplied ? onUndo : onApply}>
        {isApplied ? undoLabel : useLabel}
      </LinkButton>
    </Flex>
  );
};

export const ORaised = ({
  findings,
  onFix,
}: {
  findings: CopilotFinding[];
  onFix: (finding: CopilotFinding) => void;
}) => {
  const { t } = useTranslation("common");
  if (findings.length === 0) return null;
  return (
    <Flex column gap="4px" data-testid="o-raised">
      {findings.map((finding) => (
        <Flex key={finding.id} gap="8px" align="center" justify="between" padding="0 12px">
          <Flex gap="6px" align="center">
            <Dot color={Color.yellow} />
            <Text variant="small1" color={Color.yellowActive}>
              {titleOf(finding)}
            </Text>
          </Flex>
          {finding.fix && (
            <LinkButton type="button" onClick={() => onFix(finding)}>
              {fixLabelOf(finding, t("configCopilot.fix"))}
            </LinkButton>
          )}
        </Flex>
      ))}
    </Flex>
  );
};

export const SuggestionChip = styledSafe("button")`
  padding: 5px 11px;
  border: 1px solid ${Color.greenSmokeHover};
  border-radius: 14px;
  background: ${Color.white};
  color: ${Color.primaryActive};
  font: inherit;
  font-size: 12px;
  white-space: nowrap;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${Color.greenSmoke};
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;
